import Dexie, { type Table } from 'dexie'

export type OutboxStatus = 'pending' | 'sending' | 'failed'

/**
 * One pending write. `id` is generated on the device and sent to the server, so a retry after a
 * dropped connection can never create a duplicate.
 */
export type OutboxItem = {
  id: string
  type: string
  payload: unknown
  /** Plain-language description shown to the rep, e.g. "Relux for Durban Warehouse". */
  label: string
  userId: string
  createdAt: number
  attempts: number
  nextAttemptAt: number
  status: OutboxStatus
  lastError?: string
}

/** A form the user has started but not submitted. Saved on every change. */
export type Draft = {
  id: string
  kind: string
  userId: string
  title: string
  data: unknown
  updatedAt: number
  /** "queued" once submitted: the draft stays on the device until the server has it. */
  status?: 'editing' | 'queued'
}

/** A photo or document held on the device until it has uploaded. */
export type LocalFile = {
  id: string
  userId: string
  draftId?: string
  name: string
  type: string
  size: number
  blob: Blob
  createdAt: number
  /** Set once uploaded, so a retry after a dropped connection skips it. */
  uploadedPath?: string
  /** Photo markup (arrows, lines, text) for re-editing. The blob is the flattened image. */
  markup?: unknown
  /** Untouched photo, kept until upload so markup can be redone. */
  original?: Blob
}

export class MezDb extends Dexie {
  outbox!: Table<OutboxItem, string>
  drafts!: Table<Draft, string>
  files!: Table<LocalFile, string>

  constructor(name = 'mezsites') {
    super(name)
    this.version(1).stores({
      outbox: 'id, status, nextAttemptAt, createdAt, userId',
      drafts: 'id, kind, userId, updatedAt',
      files: 'id, userId, draftId, createdAt',
    })
  }
}

export const db = new MezDb()
